import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { useAirQualityStore } from '@/stores/useAirQualityStore';

interface VideoOverlayScreenProps {
  isOpen: boolean;
  onClose: () => void;
  elapsed: number;
}

const BREATHS_PER_MIN_RUN = 30;

/** Calculate Blurp.ie Air Score: 0-100 (higher = better) */
function calcAirScore(aqiVal: number, paceMinPerKm: number): number {
  const breathMultiplier = paceMinPerKm < 5 ? 1.3 : paceMinPerKm < 6 ? 1.0 : 0.8;
  const raw = 100 - (aqiVal * breathMultiplier * 0.6);
  return Math.max(0, Math.min(100, Math.round(raw)));
}

function getScoreLevel(score: number): 'great' | 'good' | 'fair' | 'poor' {
  if (score >= 80) return 'great';
  if (score >= 60) return 'good';
  if (score >= 40) return 'fair';
  return 'poor';
}

const SCORE_COLORS = {
  great: '#64c88c',
  good: '#78be82',
  fair: '#dcb45a',
  poor: '#d27864',
};

const STATUS_TEXT = {
  great: 'Great run for your lungs',
  good: 'Good conditions to run',
  fair: 'Moderate air exposure',
  poor: 'High pollution intake',
};

const RING_CIRCUMFERENCE = 2 * Math.PI * 72;

export function VideoOverlayScreen({ isOpen, onClose, elapsed }: VideoOverlayScreenProps) {
  const { recordingData } = useRunningStore();
  const airQuality = useAirQualityStore((s) => s.currentReading);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const scoreHistoryRef = useRef<number[]>([]);
  const cumulativeIntakeRef = useRef(0);

  const [isVideoRecording, setIsVideoRecording] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [cameraError, setCameraError] = useState<string | null>(null);

  // Start camera
  const startCamera = useCallback(async (facing: 'user' | 'environment') => {
    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1080 }, height: { ideal: 1920 } },
        audio: true,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraError(null);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Camera access denied. Please allow camera permissions.');
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      startCamera(facingMode);
    }
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, [isOpen, startCamera, facingMode]);

  const flipCamera = useCallback(() => {
    setFacingMode(f => f === 'user' ? 'environment' : 'user');
  }, []);

  // Compute live stats
  const aqi = airQuality?.aqi || 0;
  const pm25 = airQuality?.pm25?.value || 0;
  const distKm = recordingData.distance / 1000;
  const paceSeconds = distKm > 0.01 ? elapsed / distKm : 0;
  const paceMinPerKm = paceSeconds / 60;
  const paceMin = Math.floor(paceSeconds / 60);
  const paceSec = Math.floor(paceSeconds % 60);
  const isSpeedType = recordingData.activityType === 'cycle';
  const speedKmh = elapsed > 0 ? (distKm / (elapsed / 3600)) : 0;
  const calories = Math.round(elapsed / 60 * (isSpeedType ? 8 : 7));

  const airScore = calcAirScore(aqi, paceMinPerKm || 5);
  const level = getScoreLevel(airScore);
  const color = SCORE_COLORS[level];

  const totalBreaths = Math.round(elapsed / 60 * BREATHS_PER_MIN_RUN);

  // Update cumulative intake each render
  useEffect(() => {
    if (elapsed > 0 && pm25 > 0) {
      cumulativeIntakeRef.current += (BREATHS_PER_MIN_RUN / 60) * 2 * (pm25 / 1000) * 0.6;
    }
  }, [elapsed, pm25]);

  // Update score history
  useEffect(() => {
    scoreHistoryRef.current.push(airScore);
    if (scoreHistoryRef.current.length > 60) scoreHistoryRef.current.shift();
  }, [airScore]);

  const lastSample = recordingData.aqi_samples[recordingData.aqi_samples.length - 1];

  // Format time
  const formatTime = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  // SVG ring offset
  const ringOffset = RING_CIRCUMFERENCE * (1 - airScore / 100);

  // Sparkline points
  const sparklinePoints = (() => {
    const hist = scoreHistoryRef.current;
    if (hist.length < 2) return '';
    const w = 140, h = 24;
    const minS = Math.min(...hist) - 3;
    const maxS = Math.max(...hist) + 3;
    const range = maxS - minS || 1;
    return hist.map((v, i) => {
      const x = (i / (hist.length - 1)) * w;
      const y = h - ((v - minS) / range) * (h - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  })();

  // Start/stop video recording using canvas compositing
  const startVideoRecording = useCallback(() => {
    if (!canvasRef.current || !videoRef.current || !streamRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 1080;
    canvas.height = 1920;

    // Composite video + overlay on canvas
    const drawFrame = () => {
      if (!videoRef.current || !ctx) return;

      // Draw camera feed
      const vw = videoRef.current.videoWidth;
      const vh = videoRef.current.videoHeight;
      if (vw && vh) {
        const scale = Math.max(canvas.width / vw, canvas.height / vh);
        const dx = (canvas.width - vw * scale) / 2;
        const dy = (canvas.height - vh * scale) / 2;
        ctx.drawImage(videoRef.current, dx, dy, vw * scale, vh * scale);
      }

      // Draw overlay elements on canvas
      drawOverlay(ctx, canvas.width, canvas.height);

      if (mediaRecorderRef.current?.state === 'recording') {
        requestAnimationFrame(drawFrame);
      }
    };

    // Get canvas stream + audio from camera
    const canvasStream = canvas.captureStream(30);
    const audioTrack = streamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      canvasStream.addTrack(audioTrack);
    }

    const recorder = new MediaRecorder(canvasStream, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm',
      videoBitsPerSecond: 5000000,
    });

    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      // Trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `blurpie-run-${new Date().toISOString().slice(0, 10)}.webm`;
      a.click();
      URL.revokeObjectURL(url);
    };

    mediaRecorderRef.current = recorder;
    recorder.start(1000);
    setIsVideoRecording(true);
    drawFrame();
  }, []);

  // Draw overlay onto canvas for recording
  const drawOverlay = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number) => {
    const scale = w / 390; // scale from phone-frame width

    // Top bar background
    ctx.fillStyle = 'rgba(0,0,0,0.85)';
    ctx.fillRect(0, 0, w, 100 * scale);

    // Brand text
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = `${9 * scale}px Inter, sans-serif`;
    ctx.letterSpacing = `${2 * scale}px`;
    ctx.fillText('BETTER AIR, BETTER RUN', 60 * scale, 60 * scale);

    // Air Score circle (center)
    const cx = w / 2;
    const cy = h * 0.42;
    const r = 72 * scale;

    // Ring background
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 3 * scale;
    ctx.stroke();

    // Ring fill
    const progress = airScore / 100;
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
    ctx.strokeStyle = color;
    ctx.lineWidth = 3 * scale;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Score number
    ctx.fillStyle = color;
    ctx.font = `500 ${44 * scale}px 'DM Mono', monospace`;
    ctx.textAlign = 'center';
    ctx.fillText(airScore.toString(), cx, cy + 12 * scale);

    // "Air Score" label
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.font = `${8 * scale}px Inter, sans-serif`;
    ctx.fillText('AIR SCORE', cx, cy + 28 * scale);

    // Status text
    ctx.fillStyle = color;
    ctx.font = `600 ${13 * scale}px Inter, sans-serif`;
    ctx.fillText(STATUS_TEXT[level], cx, cy + r + 30 * scale);

    // Bottom panel
    const bottomY = h - 200 * scale;
    ctx.fillStyle = 'rgba(0,0,0,0.85)';
    ctx.fillRect(0, bottomY, w, 200 * scale);

    // Timer
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = `${36 * scale}px 'DM Mono', monospace`;
    ctx.textAlign = 'center';
    ctx.fillText(formatTime(elapsed), cx, bottomY + 50 * scale);

    // Stats row
    const statsY = bottomY + 90 * scale;
    ctx.font = `500 ${18 * scale}px 'DM Mono', monospace`;
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(distKm.toFixed(2), w * 0.2, statsY);
    ctx.fillText(
      isSpeedType ? speedKmh.toFixed(1) : (paceSeconds > 0 ? `${paceMin}:${paceSec.toString().padStart(2, '0')}` : '--:--'),
      cx, statsY
    );
    ctx.fillText(calories.toString(), w * 0.8, statsY);

    // Labels
    ctx.font = `${8 * scale}px Inter, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillText('KM', w * 0.2, statsY + 18 * scale);
    ctx.fillText(isSpeedType ? 'KM/H' : 'PACE', cx, statsY + 18 * scale);
    ctx.fillText('CAL', w * 0.8, statsY + 18 * scale);

    ctx.textAlign = 'start';
  }, [airScore, color, level, elapsed, distKm, paceMin, paceSec, paceSeconds, isSpeedType, speedKmh, calories]);

  const stopVideoRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsVideoRecording(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isVideoRecording) stopVideoRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    onClose();
  }, [isVideoRecording, stopVideoRecording, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black"
      >
        {/* Camera feed */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted
        />

        {/* Hidden canvas for recording composite */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Camera error */}
        {cameraError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20 px-8">
            <div className="text-center">
              <svg className="w-12 h-12 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400 text-sm">{cameraError}</p>
              <button onClick={handleClose} className="mt-4 px-6 py-2 bg-white/10 rounded-lg text-white text-sm">
                Go Back
              </button>
            </div>
          </div>
        )}

        {/* ===== OVERLAY ===== */}

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 bg-black/85 pt-12 pb-3 px-5 z-10 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="Blurp.ie"
                className="w-7 h-7 rounded-lg"
                style={{ mixBlendMode: 'screen' }}
              />
              <span className="text-[9px] text-white/60 font-medium uppercase tracking-[2px]">
                Better Air, Better Run
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-[5px] h-[5px] rounded-full bg-red-500/80 animate-pulse" />
              <span className="text-[9px] text-white/35 uppercase tracking-[1.5px] font-medium">
                {isVideoRecording ? 'REC' : 'LIVE'}
              </span>
            </div>
          </div>
        </div>

        {/* Air Score Hero — center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center pointer-events-none" style={{ marginTop: '-20px' }}>
          {/* Ring */}
          <div className="relative w-40 h-40 mx-auto mb-3">
            <svg viewBox="0 0 160 160" className="w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
              <circle
                cx="80" cy="80" r="72"
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
                style={{ transition: 'stroke-dashoffset 1s ease, stroke 0.8s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-[44px] font-medium leading-none" style={{ color, transition: 'color 0.8s ease' }}>
                {airScore}
              </span>
              <span className="text-[8px] text-white/25 uppercase tracking-[3px] mt-1">Air Score</span>
            </div>
          </div>

          {/* Status */}
          <div className="text-[13px] font-semibold tracking-wide mb-1.5" style={{ color, transition: 'color 0.8s ease' }}>
            {STATUS_TEXT[level]}
          </div>

          {/* Breath stats row */}
          <div className="flex justify-center gap-6 mt-4 pt-3 border-t border-white/[0.06]">
            <div className="text-center">
              <div className="font-mono text-base font-medium text-white/80">{totalBreaths.toLocaleString()}</div>
              <div className="text-[7px] text-white/25 uppercase tracking-[1.5px] mt-0.5">Breaths</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-base font-medium text-white/80">{cumulativeIntakeRef.current.toFixed(1)}</div>
              <div className="text-[7px] text-white/25 uppercase tracking-[1.5px] mt-0.5">&micro;g inhaled</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-base font-medium text-white/80">{aqi || '--'}</div>
              <div className="text-[7px] text-white/25 uppercase tracking-[1.5px] mt-0.5">AQI now</div>
            </div>
          </div>

          {/* Sparkline */}
          {sparklinePoints && (
            <div className="mt-3">
              <div className="text-[7px] text-white/[0.18] uppercase tracking-[2px] mb-1">Air Score over time</div>
              <svg width="140" height="24" viewBox="0 0 140 24" className="mx-auto block">
                <polyline
                  points={sparklinePoints}
                  fill="none"
                  stroke={color}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ transition: 'stroke 0.8s ease' }}
                />
              </svg>
            </div>
          )}
        </div>

        {/* Blurp.ie Branding - Middle Right */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 z-10 flex flex-col items-center pointer-events-none">
          <img
            src="/logo-transparent.png"
            alt="Blurp.ie"
            className="w-24 h-24 mb-2"
            style={{ filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.5))' }}
          />
          <span className="text-white font-bold text-xl tracking-wide" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
            Blurp.ie
          </span>
        </div>

        {/* Bottom panel */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-10 pb-8 px-6 z-10">
          {/* Timer */}
          <div className="text-center mb-4">
            <span className="font-mono text-[40px] font-normal text-white/[0.92] tracking-wider">
              {formatTime(elapsed)}
            </span>
            <div className="text-[8px] text-white/25 uppercase tracking-[4px] mt-1">elapsed</div>
          </div>

          {/* Stats row */}
          <div className="flex justify-center gap-8 mb-4">
            <div className="text-center">
              <div className="font-mono text-xl font-medium text-white/85">{distKm.toFixed(2)}</div>
              <div className="text-[8px] text-white/30 uppercase tracking-[2px] mt-1">km</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-xl font-medium text-white/85">
                {isSpeedType
                  ? speedKmh.toFixed(1)
                  : paceSeconds > 0 ? `${paceMin}:${paceSec.toString().padStart(2, '0')}` : '--:--'
                }
              </div>
              <div className="text-[8px] text-white/30 uppercase tracking-[2px] mt-1">{isSpeedType ? 'km/h' : 'pace'}</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-xl font-medium text-white/85">{calories}</div>
              <div className="text-[8px] text-white/30 uppercase tracking-[2px] mt-1">cal</div>
            </div>
          </div>

          {/* Environmental strip */}
          <div className="flex justify-center gap-4 pt-3 border-t border-white/[0.06] mb-6">
            <div className="flex items-center gap-1 text-[9px]">
              <span className="font-mono text-white/45 font-medium">
                {lastSample?.temperature != null ? `${Math.round(lastSample.temperature)}°` : '--'}
              </span>
              <span className="text-white/22">C</span>
            </div>
            <div className="flex items-center gap-1 text-[9px]">
              <span className="text-white/22">PM2.5</span>
              <span className="font-mono text-white/45 font-medium">{pm25 ? Math.round(pm25) : '--'}</span>
            </div>
            <div className="flex items-center gap-1 text-[9px]">
              <span className="font-mono text-white/45 font-medium">
                {lastSample?.humidity != null ? Math.round(lastSample.humidity) : '--'}
              </span>
              <span className="text-white/22">%rh</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-5">
            {/* Flip camera */}
            <button
              onClick={flipCamera}
              className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Record / Stop */}
            <button
              onClick={isVideoRecording ? stopVideoRecording : startVideoRecording}
              className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg ${
                isVideoRecording
                  ? 'bg-red-500 shadow-red-500/30'
                  : 'bg-white/90 shadow-white/20'
              }`}
            >
              {isVideoRecording ? (
                <div className="w-6 h-6 rounded-sm bg-white" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-red-500" />
              )}
            </button>

            {/* Close */}
            <button
              onClick={handleClose}
              className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
